import { useContext, useEffect } from "react";
import { SocketContext } from "../../chat/websocket/Provider/SocketProvider";
import { useModal } from "../ModalProvider/ModalProvider";
import { isNotificationCommand } from "../interfaces/NotificationBridgeInterface";
import { useToast } from "../ToastProvider/ToastProvider";
import {useSelector} from "react-redux";

/**
 * NotificationBridge listens to incoming WebSocket messages
 * and triggers UI actions such as showing modals or toasts.
 *
 * This component must be placed inside the {@link SocketProvider}
 * and {@link ModalProvider}.
 *
 * @component
 */
export default function NotificationBridge() {
    const socket = useContext(SocketContext);
    const { openModal } = useModal();
    const { showToast } = useToast();

    const currentUserId = useSelector((state: any) => state.user.id);

    useEffect(() => {
        if (!socket) return;

        /**
         * WebSocket message handler.
         *
         * @param {MessageEvent} event - The WebSocket message event containing JSON data.
         */
        const handler = (event: MessageEvent) => {
            let cmds: unknown;
            try {
                cmds = JSON.parse(event.data);
            } catch {
                return;
            }

            (Array.isArray(cmds) ? cmds : [cmds]).forEach((cmd: any) => {
                if (!isNotificationCommand(cmd)) return; // Ignore invalid payloads

                // Only handle messages intended for the current user
                if (cmd.user && String(cmd.user) !== currentUserId) {
                    return;
                }

                switch (cmd.func) {
                    case "showModal":
                        openModal(cmd);
                        break;
                    case "showToast":
                        showToast(cmd);
                        break;
                    default:
                        console.warn("Unknown func:", cmd.func);
                }
            });
        };

        socket.addEventListener("message", handler);
        return () => socket.removeEventListener("message", handler);
    }, [socket, openModal]);

    return null;
}
